import type { ReactNode } from "react";
import type { Health } from "../data/model";
import { cn } from "@/v2/lib/utils";

const HEALTH_DOT: Record<Health, string> = {
    healthy: "bg-ok",
    degraded: "bg-warn",
    down: "bg-bad",
    idle: "bg-idle",
    unknown: "bg-transparent ring-1 ring-inset ring-idle",
};

export const HEALTH_LABEL: Record<Health, string> = {
    healthy: "Healthy",
    degraded: "Degraded",
    down: "Down",
    idle: "No pods",
    unknown: "Unknown",
};

export function HealthDot({ health, className }: { health: Health; className?: string }) {
    return <span aria-hidden className={cn("inline-block size-2 shrink-0 rounded-full", HEALTH_DOT[health], className)} />;
}

/**
 * Keeps the start and the distinctive end of long names, e.g. businessev…handling-p5jkb5.
 * `noTitle` when a tooltip around it already shows the full text.
 */
export function MiddleTruncate({ text, max = 28, className, noTitle }: { text: string; max?: number; className?: string; noTitle?: boolean }) {
    if (text.length <= max) return <span className={className}>{text}</span>;
    const tail = Math.ceil((max - 1) * 0.45);
    const head = max - 1 - tail;
    return (
        <span className={className} title={noTitle ? undefined : text} aria-label={text}>
            {text.slice(0, head)}…{text.slice(-tail)}
        </span>
    );
}

const MINUTE = 60 * 1000, HOUR = 60 * MINUTE, DAY = 24 * HOUR;

export function relativeTime(iso?: string, now = Date.now()): string {
    if (!iso) return "";
    const diff = now - new Date(iso).getTime();
    if (diff < MINUTE) return "just now";
    if (diff < HOUR) return `${Math.round(diff / MINUTE)} min ago`;
    if (diff < DAY) return `${Math.round(diff / HOUR)} h ago`;
    if (diff < 7 * DAY) return `${Math.round(diff / DAY)} days ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function Time({ iso }: { iso?: string }) {
    if (!iso) return null;
    return <time dateTime={iso} title={new Date(iso).toLocaleString()}>{relativeTime(iso)}</time>;
}

export function Mono({ children, className, title }: { children: ReactNode; className?: string; title?: string }) {
    return <span title={title} className={cn("font-mono text-[12px]", className)}>{children}</span>;
}

export function Pill({ tone = "neutral", children, className }: { tone?: "neutral" | "ok" | "warn" | "bad" | "info"; children: ReactNode; className?: string }) {
    const tones = {
        neutral: "bg-soft text-text-2",
        ok: "bg-ok-tint text-ok-ink",
        warn: "bg-warn-tint text-warn-ink",
        bad: "bg-bad-tint text-bad-ink",
        info: "bg-lagoon-tint text-lagoon-ink",
    };
    return <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap", tones[tone], className)}>{children}</span>;
}

export function Tile({ children, className }: { children: ReactNode; className?: string }) {
    return <section className={cn("rounded-2xl bg-tile p-6 shadow-tile", className)}>{children}</section>;
}
